/**
 * Cliente del API cross-tenant de plataforma (Doc 25 §6.2).
 *
 * Endpoints reservados a `platform_admin`. El backend bloquea con 403
 * a cualquier user sin el flag.
 *
 * Endpoints:
 *   GET /platform/tenants                        — lista con KPIs y estado.
 *   GET /platform/tenants/{id}/usage/summary     — summary scoped a un tenant.
 *   GET /platform/usage/global                   — agregado plataforma.
 *   GET /platform/usage/margin                   — tabla margen por tenant.
 *
 * Consola de clientes (OT-0073 §4.2): alta, detalle e historial, y las
 * acciones de ciclo de vida. Cada acción responde el detalle actualizado.
 */

import { apiFetch } from './client'

/** `tenants.status` del backend. */
export type TenantStatus = 'trial' | 'active' | 'suspended'

/** Acceso calculado por el backend (`permissions.tenant_access`); el
 * frontend no lo deduce de `status` + `trial_ends_at`. */
export type TenantAccess = 'full' | 'read_only' | 'suspended'

/** Si el administrador del cliente ya entró (`TenantOverviewService`). */
export type TenantAdminState = 'active' | 'invited' | 'expired'

export type TenantPlanTier = 'free' | 'pro' | 'enterprise'

export interface PlatformTenantListRow {
  tenant_id: string
  slug: string
  display_name: string
  balance_usd: string | null
  total_consumed_usd: string | null
  markup_percent: string | null
  last_consumption_at: string | null
  last_topup_at: string | null
  status: TenantStatus
  access: TenantAccess
  trial_ends_at: string | null
  members: number
  admin_state: TenantAdminState
  last_activity_at: string | null
}

export interface PlatformTenantDefaults {
  trial_months: number
  credit_usd: string
  max_trial_months: number
  max_credit_usd: string
}

export interface PlatformTenantCreate {
  slug: string
  display_name: string
  admin_email: string
  default_locale: 'es' | 'en'
  trial_months: number
  credit_usd: string
}

export interface PlatformTenantCredit {
  amount_usd: string
  payment_method: string
  notes: string | null
  created_at: string
  created_by: string | null
}

/** Evento `tenant.*` de la auditoría del cliente; `details` es el payload
 * tal cual (`reason`, `source`, valores antes/después). */
export interface PlatformTenantLifecycleEvent {
  event_type: string
  occurred_at: string
  actor: string | null
  details: Record<string, unknown>
}

export interface PlatformTenantDetail {
  tenant_id: string
  slug: string
  display_name: string
  status: TenantStatus
  access: TenantAccess
  trial_ends_at: string | null
  plan_tier: TenantPlanTier
  default_locale: string
  is_platform_operator: boolean
  created_at: string
  balance_usd: string | null
  members: number
  admin_state: TenantAdminState
  admin_email: string | null
  admin_invite_expires_at: string | null
  last_activity_at: string | null
  credits: PlatformTenantCredit[]
  lifecycle: PlatformTenantLifecycleEvent[]
}

/** Acciones de ciclo de vida que piden motivo. */
export type TenantLifecycleAction =
  | { kind: 'extend_trial'; until: string; reason: string }
  | { kind: 'grant_credit'; amount_usd: string; reason: string }
  | { kind: 'suspend'; reason: string }
  | { kind: 'reactivate'; reason: string }
  | { kind: 'convert_to_active'; reason: string }

export interface PlatformUsageGlobal {
  period_from: string
  period_to: string
  tenants_active: number
  calls: number
  tokens_total: number
  cost_provider_usd: string
  cost_billed_usd: string
  margin_usd: string
  margin_pct: string | null
}

export interface PlatformMarginRow {
  tenant_id: string
  slug: string
  display_name: string
  markup_percent: string | null
  calls: number
  cost_provider_usd: string
  cost_billed_usd: string
  margin_usd: string
  margin_pct: string | null
  balance_usd: string | null
  last_topup_at: string | null
}

export interface PlatformMargin {
  period_from: string
  period_to: string
  rows: PlatformMarginRow[]
}

export interface PlatformFilters {
  from?: string
  to?: string
}

function _qs(params: Record<string, string | undefined>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  )
  if (entries.length === 0) return ''
  const sp = new URLSearchParams()
  for (const [k, v] of entries) sp.set(k, String(v))
  return `?${sp.toString()}`
}

export async function listPlatformTenants(
  accessToken: string,
): Promise<PlatformTenantListRow[]> {
  return apiFetch<PlatformTenantListRow[]>('/api/v1/platform/tenants', {
    accessToken,
  })
}

export async function getPlatformGlobal(
  accessToken: string,
  filters: PlatformFilters = {},
): Promise<PlatformUsageGlobal> {
  const qs = _qs({ from: filters.from, to: filters.to })
  return apiFetch<PlatformUsageGlobal>(
    `/api/v1/platform/usage/global${qs}`,
    { accessToken },
  )
}

export async function getPlatformMargin(
  accessToken: string,
  filters: PlatformFilters = {},
): Promise<PlatformMargin> {
  const qs = _qs({ from: filters.from, to: filters.to })
  return apiFetch<PlatformMargin>(
    `/api/v1/platform/usage/margin${qs}`,
    { accessToken },
  )
}

export async function getPlatformTenantSummary(
  accessToken: string,
  tenantId: string,
  filters: PlatformFilters = {},
): Promise<PlatformUsageGlobal> {
  const qs = _qs({ from: filters.from, to: filters.to })
  return apiFetch<PlatformUsageGlobal>(
    `/api/v1/platform/tenants/${tenantId}/usage/summary${qs}`,
    { accessToken },
  )
}

/**
 * Maintenance (Doc 25 §F5).
 *
 * El cron real corre en F.pre como ACA Job — ver
 * `hivanexus-infra/Docs/14-AZURE_DEPLOYMENT.md` §"Scheduled Jobs (F.pre
 * cabling)" y el TODO block en
 * `hivanexus-backend/app/tasks/maintenance.py`. Hoy estos endpoints son
 * el disparo manual del platform admin.
 */
export interface BalanceCheckResult {
  tenants_evaluated: number
  tenants_alerted: number
  tenants_skipped_cooldown: number
  emails_sent: number
  emails_failed: number
}

export interface BillingDriftRow {
  tenant_id: string
  tenant_slug: string
  recorded_consumed_usd: string
  records_sum_usd: string
  delta_usd: string
}

export interface BillingVerificationResult {
  tenants_evaluated: number
  drifts_detected: number
  drifts: BillingDriftRow[]
}

export async function runBalanceCheck(
  accessToken: string,
): Promise<BalanceCheckResult> {
  return apiFetch<BalanceCheckResult>(
    '/api/v1/platform/maintenance/run-balance-check',
    { accessToken, method: 'POST' },
  )
}

export async function runBillingVerification(
  accessToken: string,
): Promise<BillingVerificationResult> {
  return apiFetch<BillingVerificationResult>(
    '/api/v1/platform/maintenance/run-billing-verification',
    { accessToken, method: 'POST' },
  )
}

// ─── Consola de clientes (OT-0073) ────────────────────────────────────────────

export async function getPlatformTenantDefaults(
  accessToken: string,
): Promise<PlatformTenantDefaults> {
  return apiFetch<PlatformTenantDefaults>('/api/v1/platform/tenants/defaults', {
    accessToken,
  })
}

export async function createPlatformTenant(
  accessToken: string,
  body: PlatformTenantCreate,
): Promise<PlatformTenantDetail> {
  return apiFetch<PlatformTenantDetail>('/api/v1/platform/tenants', {
    accessToken,
    method: 'POST',
    body,
  })
}

export async function getPlatformTenantDetail(
  accessToken: string,
  tenantId: string,
): Promise<PlatformTenantDetail> {
  return apiFetch<PlatformTenantDetail>(`/api/v1/platform/tenants/${tenantId}`, {
    accessToken,
  })
}

const ACTION_PATH: Record<TenantLifecycleAction['kind'], string> = {
  extend_trial: 'extend-trial',
  grant_credit: 'credits',
  suspend: 'suspend',
  reactivate: 'reactivate',
  convert_to_active: 'convert-to-active',
}

export async function runPlatformTenantAction(
  accessToken: string,
  tenantId: string,
  { kind, ...body }: TenantLifecycleAction,
): Promise<PlatformTenantDetail> {
  return apiFetch<PlatformTenantDetail>(
    `/api/v1/platform/tenants/${tenantId}/${ACTION_PATH[kind]}`,
    { accessToken, method: 'POST', body },
  )
}

export async function resendPlatformTenantAdminInvite(
  accessToken: string,
  tenantId: string,
): Promise<PlatformTenantDetail> {
  return apiFetch<PlatformTenantDetail>(
    `/api/v1/platform/tenants/${tenantId}/resend-admin-invite`,
    { accessToken, method: 'POST' },
  )
}
